import cn from 'classnames';
import { Spinner } from '#assets/ReactSvg.ts';
import { ArrowLeftButton, ArrowRightButton } from '#components/atoms/buttons/icon/index.ts';
import type { Metadata, MetadataNoCounts } from '#dn-types/api.ts';
import styles from './PaginationControls.module.css';

type Props = {
  metadata: Metadata | MetadataNoCounts;
  isFetching: boolean;
  onNextPage: () => void;
  onPrevPage: () => void;
  /** The (plural) entity type being displayed in the table. */
  entityType?: string;
};

export function PaginationControls({ metadata, onNextPage, onPrevPage, isFetching, entityType = '' }: Props) {
  const totalCount = metadata.totalCount;
  const hasNextPage = metadata.hasNextPage;
  const hasPrevPage = metadata.hasPrevPage;
  const start = metadata.page?.start;
  const count = metadata.page?.count;

  let range: string | undefined;

  // We have counts, so we can show them
  if (start !== undefined && count !== undefined && totalCount !== undefined) {
    range = `${(start + 1).toLocaleString()}-${(
      start + count
    ).toLocaleString()} of ${totalCount.toLocaleString()} ${getEntityName(entityType, totalCount)}`.trim();
  }
  // We have total count, and full results were sent
  else if (!hasPrevPage && !hasNextPage && totalCount !== undefined) {
    range = `${totalCount.toLocaleString()} ${getEntityName(entityType, totalCount)}`.trim();
  }

  return (
    <div className={cn('align-center', styles.PaginationControls)}>
      <ArrowLeftButton
        className={styles.PaginationControls_prevButton}
        title="Previous page"
        disabled={!hasPrevPage}
        onClick={onPrevPage}
      >
        Prev
      </ArrowLeftButton>
      <ArrowRightButton
        className={styles.PaginationControls_nextButton}
        title="Next page"
        disabled={!hasNextPage}
        onClick={onNextPage}
      >
        Next
      </ArrowRightButton>
      <div className={cn(styles.PaginationControls_pageInfo)}>
        {isFetching ?
          <Spinner size={24} />
        : range && <p className="p">{range}</p>}
      </div>
    </div>
  );
}

/**
 * {@link getEntityName} is a naive function to strip a trailing `s` off a word when there's only
 * one thing.
 *
 * TODO: develop a more clever way to handle single/plural entity types
 */
function getEntityName(entityType: string, qty: number) {
  if (qty === 1) return entityType.replace(/s$/, '');
  return entityType;
}
